import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, MessageCircle, CreditCard } from "lucide-react";
import { useLang, WHATSAPP_URL } from "@/lib/i18n";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
  it: {
    eyebrow: "Tariffe",
    title: "Disponibilità e tariffe",
    p: "Le tariffe sono indicate a settimana (7 notti). Per soggiorni diversi, scrivici: troviamo la soluzione giusta per te.",
    week: "Settimana dal",
    onReq: "Su richiesta",
    cta: "Richiedi questa settimana",
    waMsg: (d) => `Ciao! Vorrei richiedere la settimana del ${d} a Private Oasis Villa.`,
    pay: "Paga acconto",
    err: "Pagamento non disponibile, riprova tra poco.",
  },
  en: {
    eyebrow: "Rates",
    title: "Availability and rates",
    p: "Rates are shown per week (7 nights). For different stays, message us: we will find the right solution for you.",
    week: "Week of",
    onReq: "On request",
    cta: "Request this week",
    waMsg: (d) => `Hello! I would like to request the week of ${d} at Private Oasis Villa.`,
    pay: "Pay deposit",
    err: "Payment unavailable, please retry shortly.",
  },
};

const fmt = (iso, lang) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

function nextSaturdays(n) {
  const out = [];
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7));
  for (let i = 0; i < n; i++) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 7);
  }
  return out;
}

export default function Rates() {
  const { lang } = useLang();
  const tx = L[lang];
  const [rates, setRates] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const payDeposit = async (week) => {
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "acconto_settimana",
        origin_url: window.location.origin,
        week,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(tx.err);
    }
  };

  useEffect(() => {
    axios_get();
    async function axios_get() {
      try {
        const { data } = await fetch(`${API}/rates`).then((r) => r.json()).then((d) => ({ data: d }));
        setRates(Array.isArray(data) ? data : []);
      } catch (e) {
        setRates([]);
      } finally {
        setLoaded(true);
      }
    }
  }, []);

  const weeks = nextSaturdays(14);
  const byWeek = Object.fromEntries(rates.map((r) => [r.week_start, r]));

  return (
    <section id="tariffe" className="border-t border-line bg-sand2 py-24 md:py-36">
      <div className="mx-auto max-w-5xl px-5 md:px-10">
        <Reveal>
          <Eyebrow>{tx.eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-serif text-4xl font-light md:text-6xl">{tx.title}</h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink2">{tx.p}</p>
        </Reveal>

        <div className="mt-12 overflow-hidden rounded-lg border border-line bg-sand" data-testid="rates-table">
          {weeks.map((week, i) => {
            const rate = byWeek[week];
            const price = rate ? Number(rate.price) : null;
            return (
              <div
                key={week}
                data-testid={`rates-row-${i}`}
                className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-8 ${
                  i !== weeks.length - 1 ? "border-b border-line" : ""
                } ${rate ? "bg-sand2/60" : ""}`}
              >
                <span className="flex items-center gap-3 text-sm font-medium text-ink md:text-base">
                  <CalendarDays className={`h-4 w-4 ${rate ? "text-terra" : "text-ink2/50"}`} />
                  {tx.week} <span className="font-serif text-lg italic">{fmt(week, lang)}</span>
                </span>
                <span className="flex items-center gap-4">
                  {price ? (
                    <span className="font-serif text-2xl font-light text-olive md:text-3xl">
                      € {price.toLocaleString(lang === "it" ? "it-IT" : "en-GB")}
                    </span>
                  ) : (
                    <span className="rounded-full border border-line px-3.5 py-1 text-[11px] uppercase tracking-[0.2em] text-ink2">
                      {tx.onReq}
                    </span>
                  )}
                  <a
                    href={`${WHATSAPP_URL}?text=${encodeURIComponent(tx.waMsg(fmt(week, lang)))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`rates-cta-${i}`}
                    className="group flex items-center gap-2 rounded-full border border-olive/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-olive transition-all duration-300 hover:bg-olive hover:text-sand"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    {tx.cta}
                  </a>
                  <button
                    data-testid={`rates-pay-${i}`}
                    onClick={() => payDeposit(week)}
                    className="flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-sand transition-colors duration-300 hover:bg-terra"
                  >
                    <CreditCard className="h-3.5 w-3.5" />
                    {tx.pay}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
        {loaded && rates.length === 0 && (
          <p className="mt-4 text-center text-xs uppercase tracking-[0.25em] text-ink2/70" data-testid="rates-empty-hint">
            —
          </p>
        )}
        <p className="mt-6 text-center text-xs text-ink2/70">
          {lang === "it"
            ? "Acconto di € 250 a prenotazione tramite Stripe · pagamento sicuro · saldo in loco o via Airbnb"
            : "€ 250 booking deposit via Stripe · secure payment · balance on site or via Airbnb"}
        </p>
      </div>
    </section>
  );
}
